import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import posts from '../../data/blog.json';

interface Post {
  id: string;
  title: string;
  url: string;
  platform: string;
  date: string;
  tags: string[];
  image: string | null;
  excerpt: string;
  readMinutes: number;
  featured: boolean;
}

const MEDIUM_PROFILE = 'https://medium.com/@sharmachaitanya945';

const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

// Newest first; the full archive (including off-topic posts) lives on /blog/.
const featured = (posts as Post[]).filter((p) => p.featured).slice(0, 6);

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

const BlogSection: React.FC = () => {
  const { themeConfig } = useTheme();

  return (
    <section id="blog" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <div className="max-w-2xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-px w-6 bg-neon-magenta/60" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-neon-magenta">
              {'// writing'}
            </span>
          </div>
          <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
            Notes from the <span className="gradient-text">build log</span>
          </h2>
          <p className="mt-3 text-lg text-ink-soft">
            How I built the things on this page, what broke along the way, and
            what I&apos;d tell someone starting out.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-3">
          <a href="/blog/" className="neon-btn-ghost">
            All posts
          </a>
          <a
            href={MEDIUM_PROFILE}
            target="_blank"
            rel="noopener noreferrer"
            className="neon-btn-ghost"
          >
            Follow on Medium
          </a>
        </div>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-100px' }}
      >
        {featured.map((post) => (
          <motion.a
            key={post.id}
            variants={item}
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            className="n8n-node group flex flex-col overflow-hidden p-0 transition-colors hover:border-neon-magenta/50"
          >
            <div className="relative h-40 overflow-hidden">
              {post.image ? (
                <img
                  src={post.image}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="flex h-full items-end bg-gradient-to-br from-neon-purple/30 via-canvas-deep to-neon-cyan/20 p-4">
                  <span className="font-mono text-xs uppercase tracking-widest text-ink-soft">
                    {post.tags[0] || post.platform}
                  </span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-canvas-node via-transparent to-transparent" />
              <span className="absolute right-3 top-3 rounded-full border border-neon-magenta/40 bg-canvas-base/80 px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-neon-magenta backdrop-blur">
                {post.platform}
              </span>
            </div>

            <div className="flex flex-1 flex-col p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                {formatDate(post.date)} · {post.readMinutes} min read
              </p>
              <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-ink-main">
                {post.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft">{post.excerpt}</p>
              <span className="mt-auto pt-4 font-mono text-xs text-neon-cyan group-hover:underline">
                read on {post.platform} →
              </span>
            </div>
          </motion.a>
        ))}
      </motion.div>
    </section>
  );
};

export default BlogSection;
